// The read-only SCOPED EXPLANATION EXPORT composition (N-FDE-3 AC-3) — a pure function that composes a
// shareable explanation artifact from the ALREADY-RENDERED Capability-map projection. It is a READ-ONLY
// composition of an existing projection: it adds NO new data, derives the artifact from the same read the
// surface renders, performs NO write, and asserts no side effect (G-154). It exposes NO credential value
// (G-140) and NO internal/build/third-party identifier beyond the owner module-directory label the projection
// already carries (G-197) — because it composes ONLY the fields the projection carries, there is nothing extra
// to leak. Kept pure + separate from the download side effect so a UI test asserts the composed content
// without touching the DOM/blob layer.

import type { CapabilityEntryView } from './operate-projections.ts';

/** The composed scoped-explanation artifact — a title + line-per-capability body, both sanitised. */
export interface ScopedExplanationArtifact {
  readonly filename: string;
  readonly body: string;
}

/**
 * Compose the scoped explanation artifact from the rendered capability entries. Each line names ONLY the
 * fields the projection carries: the operational summary, the kind, the owner, the effect, and whether it
 * crosses the Fence — the same operational descriptions the surface shows (G-169). No new data, no write.
 */
export function composeScopedExplanation(
  capabilities: readonly CapabilityEntryView[],
): ScopedExplanationArtifact {
  const header = 'What Ritona can do — capability map (read-only explanation)';
  const lines = capabilities.map((cap) => {
    // Label by the ACTUAL effect: gated crosses the Fence; an ungated read is read-only; an ungated WRITE
    // stays inside the Fence (not gated) — never mislabel a write as read-only (matches the rendered table).
    const fence = cap.gated
      ? 'gated (crosses the Fence)'
      : cap.effect === 'read'
        ? 'read-only'
        : 'not gated';
    return `- ${cap.summary} — ${cap.kind}, owner ${cap.owner}, ${cap.effect}, ${fence}`;
  });
  return {
    filename: 'ritona-capability-map.txt',
    body: [header, '', ...lines, ''].join('\n'),
  };
}
